import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Confirm, Avatar, ColorPicker, GROUP_COLORS, Spinner } from '../components/UI';

export default function UsersPage() {
  const { user: me } = useAuth();
  const toast = useToast();
  const [users,     setUsers]    = useState([]);
  const [groups,    setGroups]   = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [showModal, setShowModal]= useState(false);
  const [editU,     setEditU]    = useState(null);
  const [delU,      setDelU]     = useState(null);

  const load = useCallback(async () => {
    try {
      const [uRes, gRes] = await Promise.all([api.get('/users'), api.get('/groups')]);
      setUsers(uRes.data);
      setGroups(gRes.data);
    } catch { toast('Failed to load users', 'error'); }
    finally  { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const userGroups = (uid) => groups.filter(g =>
    Array.isArray(g.members) && g.members.some(m => m?.id === uid)
  );

  if (loading) return <><div className="topbar"><h2>Users</h2></div><div className="page"><Spinner /></div></>;

  return (
    <>
      <div className="topbar">
        <h2>Users</h2>
        <div className="topbar-right">
          <button className="btn btn-primary btn-sm" onClick={() => { setEditU(null); setShowModal(true); }}>
            ＋ Add User
          </button>
        </div>
      </div>

      <div className="page">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          {users.map(u => {
            const ug = userGroups(u.id);
            return (
              <div key={u.id} style={{
                background: 'var(--s1)', border: '1px solid var(--border)',
                borderRadius: 'var(--r)', padding: '14px 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar username={u.username} color={u.color} size={36} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
                      {u.username}
                      {u.id === me.id && <span style={{ fontSize: 11, color: 'var(--accent)' }}>● you</span>}
                      {!u.is_active && <span className="badge b-red">inactive</span>}
                    </div>
                    <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className={`badge ${u.role === 'admin' ? 'b-purple' : 'b-blue'}`} style={{ fontSize: 10 }}>
                        {u.role}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--sub)' }}>
                        Joined {new Date(u.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {ug.length > 0 && (
                      <div style={{ marginTop: 5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {ug.map(g => (
                          <span key={g.id} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: 11, background: 'var(--s3)', border: '1px solid var(--border2)',
                            borderRadius: 20, padding: '2px 8px', fontWeight: 600,
                          }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: g.color }} />
                            {g.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditU(u); setShowModal(true); }}>
                    Edit
                  </button>
                  {u.id !== me.id && (
                    <button className="btn btn-danger btn-sm" onClick={() => setDelU(u)}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showModal && (
        <UserModal
          item={editU}
          onClose={() => { setShowModal(false); setEditU(null); }}
          onSave={async (data) => {
            try {
              if (editU) {
                await api.put(`/users/${editU.id}`, data);
                toast('✅ User updated');
              } else {
                await api.post('/users', data);
                toast('✅ User created');
              }
              setShowModal(false); setEditU(null); load();
            } catch (e) { toast(e.response?.data?.error || 'Save failed', 'error'); }
          }}
        />
      )}

      {delU && (
        <Confirm
          title="Delete User" danger
          msg={<>Delete <strong>"{delU.username}"</strong>? They will lose access to all groups.</>}
          confirmLabel="Delete User"
          onCancel={() => setDelU(null)}
          onConfirm={async () => {
            try { await api.delete(`/users/${delU.id}`); toast('🗑 User deleted'); load(); }
            catch { toast('Delete failed', 'error'); }
            setDelU(null);
          }}
        />
      )}
    </>
  );
}

function UserModal({ item, onClose, onSave }) {
  const [username, setUsername] = useState(item?.username || '');
  const [pw,       setPw]       = useState('');
  const [role,     setRole]     = useState(item?.role     || 'user');
  const [color,    setColor]    = useState(item?.color    || GROUP_COLORS[Math.floor(Math.random() * GROUP_COLORS.length)]);
  const [showPw,   setShowPw]   = useState(false);
  const [saving,   setSaving]   = useState(false);

  const submit = async () => {
    if (!username.trim()) { alert('Username required.'); return; }
    if (!item && !pw.trim()) { alert('Password required.'); return; }
    setSaving(true);
    const data = { username, role, color };
    if (pw.trim()) data.password = pw;
    await onSave(data);
    setSaving(false);
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <h3>{item ? 'Edit User' : 'Add User'}</h3>

        <div className="field">
          <label className="lbl">Username *</label>
          <input
            value={username} onChange={e => setUsername(e.target.value)}
            placeholder="johndoe" disabled={!!item}
          />
        </div>

        <div className="field">
          <label className="lbl">{item ? 'New Password (leave blank to keep)' : 'Password *'}</label>
          <div className="pw-wrap">
            <input
              type={showPw ? 'text' : 'password'}
              value={pw} onChange={e => setPw(e.target.value)}
              placeholder="••••••••"
            />
            <button className="pw-toggle" onClick={() => setShowPw(s => !s)}>{showPw ? '🙈' : '👁'}</button>
          </div>
        </div>

        <div className="grid2">
          <div className="field">
            <label className="lbl">Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="field">
            <label className="lbl">Avatar Color</label>
            <ColorPicker value={color} onChange={setColor} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}
